import { Box, Chip, IconButton, Paper, Skeleton, Typography, useTheme } from '@mui/material';
import { useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { getStatusConfig } from '@verantos-dev/feature-annotation-screen';
import {
  ANNOTATION_STATUS_ACCEPTED,
  ANNOTATION_STATUS_ADDED_BY_SUPERVISOR,
} from '@verantos-dev/feature-annotation-screen';
import { Icon } from '@verantos-dev/shared-components';
import { ErrorAlertCard } from '@verantos-dev/ui-annotation-tool';

import { useResolvedCard } from './useResolvedCard';
import { useSupervisorMode } from '../../../../context/SupervisorModeContext';
import { AnnotationWithAnnotatorName } from '../../../../types/types';
import { AnnotatorAnnotationCard } from '../AnnotatorTab/AnnotatorAnnotationCard';
import AnnotatorEvidencesList from '../EditAndAddAnnotations/AnnotatorEvidencesList';
import { ReferenceTextSection } from '../ReferenceTextSection';

export interface ResolvedCardProps {
  annotation: AnnotationWithAnnotatorName;
  conceptId: string;
  annotationNumber: number;
  onDelete: (e: React.MouseEvent<HTMLButtonElement>, annotationId: string) => void;
  isDeleteResolvedAnnotationLoading: boolean;
  deleteError?: string | null;
  onDismissDeleteError?: () => void;
}

export const ResolvedCard = ({
  annotation,
  conceptId,
  annotationNumber,
  onDelete,
  isDeleteResolvedAnnotationLoading,
  deleteError,
  onDismissDeleteError,
}: ResolvedCardProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { isReadOnly } = useSupervisorMode();
  const { handleAddToEditMode } = useResolvedCard({ annotationId: annotation.annotationId });
  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (deleteError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [deleteError]);

  if (isDeleteResolvedAnnotationLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(2),
          width: '100%',
          border: `1px solid ${theme.palette.divider}`,
          p: theme.spacing(4),
        }}
      >
        <Skeleton variant="text" width="100%" height="100px" />
      </Box>
    );
  }

  const statusConfig = getStatusConfig(annotation.status, theme);

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        backgroundColor: statusConfig.bgColor,
        border: `1px solid ${statusConfig.borderColor}`,
        padding: theme.spacing(4),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(3) }}>
          <Typography variant="body1">
            {formatMessage(
              { id: 'supervisorReview.tab.annotationCount' },
              { count: annotationNumber }
            )}
          </Typography>
          {statusConfig.icon}
          <Chip
            variant="filled"
            size="small"
            sx={{
              backgroundColor: statusConfig.chipBg,
              color: statusConfig.chipLabelColor,
              fontSize: '13px',
            }}
            label={formatMessage({ id: statusConfig.labelKey })}
          />
          {annotation.spans.length === 0 && (
            <ReferenceTextSection referenceText={annotation.referenceText} />
          )}
        </Box>
        {!isReadOnly && (
          <Box>
            <IconButton onClick={(e) => handleAddToEditMode(e)} sx={{}}>
              <Icon
                id={`edit-resolved-${annotation.annotationId}`}
                icon="edit"
                width={19}
                height={19}
              />
            </IconButton>
            <IconButton onClick={(e) => onDelete(e, annotation.annotationId)}>
              <Icon
                id={`delete-resolved-${annotation.annotationId}`}
                icon="delete"
                width={19}
                height={19}
              />
            </IconButton>
          </Box>
        )}
      </Box>

      <AnnotatorAnnotationCard annotation={annotation} />
      <AnnotatorEvidencesList
        annotation={annotation}
        editingAnnotationId={annotation.annotationId}
        conceptId={conceptId}
      />

      <Box ref={errorRef}>
        <ErrorAlertCard
          errorMessage={deleteError ?? ''}
          onDismiss={
            onDismissDeleteError ??
            (() => {
              /* noop */
            })
          }
        />
      </Box>
    </Paper>
  );
};
